if (!customElements.get('quick-view-modal')) {
  class QuickViewModal extends HTMLElement {
    constructor() {
      super();
      this.contentEl = this.querySelector('.js-quick-view-content');
      this.loadingEl = this.querySelector('.quick-view-modal__loading');
      this.closeBtn = this.querySelector('.js-close-quick-view');
      this.overlay = this.querySelector('.quick-view-modal__overlay');
      this.fetch = null;
      this.fetchedUrls = [];
      this.productUrl = null;
      this.productEl = null;

      this.closeBtn.addEventListener('click', () => this.close());
      this.overlay.addEventListener('click', () => this.close());
      this.quickViewButtonMouseEnterHandler = this.handleMouseEnter.bind(this);
      this.documentClickHandler = this.handleDocumentClick.bind(this);

      document.addEventListener('click', this.documentClickHandler);
      this.addEventListener('on:variant:change', this.handleVariantChange.bind(this));

      // Prefetch on hover for performance
      if (theme.device.hasHover && theme.mediaMatches.md) {
        document.querySelectorAll('.js-quick-view').forEach((btn) => {
          this.bindMouseEnter(btn);
        });

        if ('MutationObserver' in window) {
          this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-view').forEach((btn) => {
                    this.bindMouseEnter(btn);
                  });
                }
              });
              mutation.removedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-view').forEach((btn) => {
                    btn.removeEventListener('mouseenter', this.quickViewButtonMouseEnterHandler);
                  });
                }
              });
            });
          });
          this.observer.observe(document.body, { childList: true, subtree: true });
        }
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.documentClickHandler);
      document.querySelectorAll('.js-quick-view').forEach((btn) => {
        btn.removeEventListener('mouseenter', this.quickViewButtonMouseEnterHandler);
      });
      if (this.observer) this.observer.disconnect();
    }

    bindMouseEnter(btn) {
      if (!btn.dataset.quickViewListenerAdded) {
        btn.dataset.quickViewListenerAdded = 'true';
        btn.addEventListener('mouseenter', this.quickViewButtonMouseEnterHandler);
      }
    }

    handleMouseEnter(evt) {
      const url = evt.target.closest('[data-product-url]')?.dataset.productUrl;
      if (url && !this.fetchedUrls.includes(url)) {
        this.fetch = { url, promise: fetch(url) };
        this.fetchedUrls.push(url);
      }
    }

    handleDocumentClick(evt) {
      const btn = evt.target.closest('.js-quick-view');
      if (!btn) return;
      evt.preventDefault();
      this.open(btn);
    }

    handleVariantChange(evt) {
      if (!evt.detail.variant) return;

      // Filter media group if grouping is enabled
      if (this.variantMediaData) {
        const groupName = this.getGroupNameForVariant(evt.detail.variant);
        if (groupName) {
          this.setActiveMediaGroup(groupName);
        }
      }

      // Update image (scroll to the variant's featured media within the visible group)
      if (evt.detail.variant.featured_media) {
        this.updateImage(evt.detail.variant.featured_media.id);
      }

      // Update price
      const priceEl = this.querySelector('.quick-view-modal__price');
      if (priceEl && evt.detail.variant) {
        const variant = evt.detail.variant;
        let priceHtml = '';
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          priceHtml = `
            <span class="price__current theme-money">${this.formatMoney(variant.price)}</span>
            <span class="price__was theme-money"><s>${this.formatMoney(variant.compare_at_price)}</s></span>
          `;
        } else {
          priceHtml = `<span class="price__current theme-money">${this.formatMoney(variant.price)}</span>`;
        }
        priceEl.innerHTML = priceHtml;
      }
    }

    formatMoney(cents) {
      const format = theme?.settings?.moneyWithCurrencyFormat;
      if (format) {
        return format
          .replace(/\{\{\s*amount\s*\}\}/, (cents / 100).toFixed(2))
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, (cents / 100).toFixed(2).replace('.', ','))
          .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, Math.round(cents / 100).toString().replace('.', ','));
      }
      return `$${(cents / 100).toFixed(2)}`;
    }

    async open(opener) {
      const productUrl = opener.dataset.productUrl;
      opener.setAttribute('aria-disabled', 'true');

      this.hidden = false;
      this.scrollY = window.scrollY;
      document.body.classList.add('fixed');
      document.body.style.top = `-${this.scrollY}px`;

      // Trap focus
      this.keyupHandler = (evt) => {
        if (evt.key === 'Escape') this.close();
      };
      document.addEventListener('keyup', this.keyupHandler);

      // If same product, just show
      if (this.productUrl === productUrl && this.contentEl.innerHTML) {
        opener.removeAttribute('aria-disabled');
        return;
      }

      this.productUrl = productUrl;
      this.contentEl.innerHTML = '';
      this.loadingEl.hidden = false;
      this.classList.add('is-loading');

      if (!this.fetch || this.fetch.url !== productUrl) {
        this.fetch = { url: productUrl, promise: fetch(productUrl) };
      }

      try {
        const response = await this.fetch.promise;
        if (response.ok) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();
          this.productEl = tmpl.content.querySelector('.js-product');
          this.fetchedContent = tmpl.content;
          this.renderProduct();
        }
      } catch (e) {
        console.error('Quick View: Failed to load product', e);
      }

      this.fetch = null;
      this.loadingEl.hidden = true;
      this.classList.remove('is-loading');
      opener.removeAttribute('aria-disabled');
    }

    close() {
      this.hidden = true;
      document.body.style.top = '';
      document.body.classList.remove('fixed');
      window.scrollTo(0, this.scrollY);
      document.removeEventListener('keyup', this.keyupHandler);
    }

    renderProduct() {
      if (!this.productEl) return;

      // Replace section IDs to avoid conflicts
      const sectionId = this.productEl.dataset.section;
      this.productEl.innerHTML = this.productEl.innerHTML.replaceAll(sectionId, 'quickview');

      // Prevent variant picker from updating URL
      const variantPicker = this.productEl.querySelector('variant-picker');
      if (variantPicker) variantPicker.dataset.updateUrl = 'false';

      // Build variant media grouping data independently from product JSON
      this.variantMediaData = null;
      this.mediaGroupMap = null;
      this.currentGroup = null;

      const variantPickerData = this.productEl.querySelector('variant-picker [type="application/json"]');
      if (variantPickerData) {
        try {
          const productData = JSON.parse(variantPickerData.textContent);
          this.variantMediaData = this.buildVariantMediaData(productData.product);
        } catch (e) {
          console.warn('Quick View: Could not parse product data for media grouping', e);
        }
      }

      // Store all media for variant switching
      this.mediaItems = this.productEl.querySelectorAll('.media-viewer__item');

      // Build slider slides from all media items
      let slidesHtml = '';
      const activeMedia = this.productEl.querySelector('.media-viewer__item.is-current-variant');
      let activeMediaId = activeMedia ? activeMedia.dataset.mediaId : null;

      if (this.mediaItems.length > 0) {
        this.mediaItems.forEach((item) => {
          const img = item.querySelector('img');
          if (img) {
            const src = (img.src || img.dataset.src || '').split('&width=')[0];
            const mediaId = item.dataset.mediaId || '';
            slidesHtml += `<div class="quick-view-slider__slide" data-media-id="${mediaId}"><img src="${src}&width=600" srcset="${src}&width=600, ${src}&width=1200 2x" alt="${img.alt || ''}" loading="lazy"></div>`;
          }
        });
      } else {
        // Fallback: try the first product image
        const firstImg = this.productEl.querySelector('.product-media img');
        if (firstImg) {
          const src = (firstImg.src || firstImg.dataset.src || '').split('&width=')[0];
          slidesHtml = `<div class="quick-view-slider__slide"><img src="${src}&width=600" srcset="${src}&width=600, ${src}&width=1200 2x" alt="${firstImg.alt || ''}" loading="lazy"></div>`;
        }
      }

      const slideCount = Array.from(this.mediaItems).filter(item => item.querySelector('img')).length;
      const hasMultipleSlides = slideCount > 1;
      const arrowsHtml = hasMultipleSlides ? `
        <button class="quick-view-slider__arrow quick-view-slider__arrow--prev js-slider-prev" type="button" aria-label="Previous image" disabled>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <button class="quick-view-slider__arrow quick-view-slider__arrow--next js-slider-next" type="button" aria-label="Next image">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
      ` : '';
      const counterHtml = hasMultipleSlides ? `
        <div class="quick-view-slider__counter">
          <span class="js-slider-current">1</span> / <span class="js-slider-total">${slideCount}</span>
        </div>
      ` : '';

      const imageHtml = `
        <div class="quick-view-slider${hasMultipleSlides ? '' : ' quick-view-slider--single'}">
          <div class="quick-view-slider__track js-slider-track">${slidesHtml}</div>
          ${arrowsHtml}
          ${counterHtml}
        </div>
      `;

      // Get product details
      const title = this.getElementHtml('.product-title') || '';
      const price = this.getElementHtml('.product-price') || '';
      const options = this.getElementHtml('.product-options') || '';
      const backorder = this.getElementHtml('.product-backorder') || '';

      // Get buy buttons form
      const productForm = this.productEl.querySelector('product-form');
      const formHtml = productForm ? productForm.innerHTML : '';

      // Get size chart modal from fetched product page (rendered outside .js-product)
      const sizeChartModal = this.fetchedContent?.querySelector('#size-chart');
      const sizeChartHtml = sizeChartModal ? sizeChartModal.outerHTML : '';

      this.contentEl.innerHTML = `
        <div class="quick-view-modal__grid js-product">
          <div class="quick-view-modal__image js-quick-view-image">
            ${imageHtml}
          </div>
          <div class="quick-view-modal__details">
            <div class="quick-view-modal__title h5 mb-2">
              <a href="${this.productUrl}" class="text-current">${title}</a>
            </div>
            <div class="quick-view-modal__price product-price mb-4">
              ${price}
            </div>
            <div class="quick-view-modal__options product-options">
              ${options}
            </div>
            <div class="quick-view-modal__backorder product-backorder">
              ${backorder}
            </div>
            <div class="quick-view-modal__form mt-4">
              <product-form>
                ${formHtml}
              </product-form>
            </div>
            <div class="quick-view-modal__success alert bg-success-bg text-success-text mt-4 js-quick-view-success" hidden>
              <p class="font-bold mb-1">${theme.strings.addedToCart || 'Added to cart!'}</p>
              <a href="${theme.routes.cart}" class="link text-current js-open-cart-drawer">${theme.strings.viewCart || 'View cart'}</a>
            </div>
            <div class="quick-view-modal__view-link mt-4 text-sm">
              <a href="${this.productUrl}" class="link">${theme.strings.viewDetails || 'View full details'}</a>
            </div>
          </div>
        </div>
        ${sizeChartHtml}
      `;

      // Init slider
      this.initSlider(activeMediaId);

      // Init form
      const newForm = this.contentEl.querySelector('product-form');
      if (newForm && newForm.init) {
        newForm.init();
      }

      if (Shopify && Shopify.PaymentButton) {
        Shopify.PaymentButton.init();
      }

      // Manually wire up modal-opener buttons (constructor fires before children are parsed via innerHTML)
      this.contentEl.querySelectorAll('modal-opener').forEach((opener) => {
        const btn = opener.querySelector('button');
        if (btn) {
          btn.addEventListener('click', () => {
            const modal = document.getElementById(opener.dataset.modal);
            if (modal) modal.open(btn);
          });
        }
      });

      // Patch size chart modal to not conflict with quick view scroll lock
      const sizeChart = this.contentEl.querySelector('#size-chart');
      if (sizeChart) {
        sizeChart.open = function(opener) {
          // Body is already fixed by quick view, just show the modal
          this.setAttribute('open', '');
          this.openedBy = opener;
          this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();
          this.addEventListener('keyup', this.keyupHandler);
        };

        sizeChart.close = function() {
          this.removeAttribute('open');
          this.removeEventListener('keyup', this.keyupHandler);
          // Don't touch body scroll — quick view still needs it locked
        };

        // Init IN/CM toggle for size chart
        const toggle = sizeChart.querySelector('#swatch-in-to-cm');
        if (toggle) {
          toggle.addEventListener('click', () => {
            const tables = sizeChart.querySelectorAll('table.new-style');
            if (tables.length === 2) {
              if (tables[0].style.display === 'none') {
                tables[0].style.display = 'table';
                tables[1].style.display = 'none';
              } else {
                tables[0].style.display = 'none';
                tables[1].style.display = 'table';
              }
            }
          });
        }
      }

      // Close quick view on add to cart success (cart drawer will open)
      this.contentEl.querySelector('product-form')?.addEventListener('on:cart:add', () => {
        this.close();
      });
    }

    showAddedToCart() {
      const success = this.querySelector('.js-quick-view-success');
      if (success) {
        success.hidden = false;
        setTimeout(() => {
          success.hidden = true;
        }, 3000);
      }
    }

    initSlider(activeMediaId) {
      this.sliderTrack = this.querySelector('.js-slider-track');
      this.sliderSlides = Array.from(this.querySelectorAll('.quick-view-slider__slide'));
      this.visibleSlides = [...this.sliderSlides]; // Tracks currently visible slides
      this.prevBtn = this.querySelector('.js-slider-prev');
      this.nextBtn = this.querySelector('.js-slider-next');
      this.counterCurrent = this.querySelector('.js-slider-current');
      this.counterTotal = this.querySelector('.js-slider-total');
      this.sliderEl = this.querySelector('.quick-view-slider');
      this.currentSlide = 0;

      // Build media group map if variant media data exists
      if (this.variantMediaData) {
        this.buildMediaGroupMap();

        // Apply initial group based on the active media
        if (activeMediaId) {
          const groupName = this.getGroupNameForMediaId(parseInt(activeMediaId, 10));
          if (groupName) {
            this.setActiveMediaGroup(groupName);
          }
        }
      }

      if (!this.sliderTrack || this.visibleSlides.length <= 1) {
        this.updateSliderVisibility();
        return;
      }

      // Scroll event to detect current slide
      this.sliderTrack.addEventListener('scroll', () => {
        if (this.visibleSlides.length <= 1) return;
        const slideWidth = this.visibleSlides[0].offsetWidth;
        if (slideWidth === 0) return;
        const newIndex = Math.round(this.sliderTrack.scrollLeft / slideWidth);
        const clampedIndex = Math.max(0, Math.min(newIndex, this.visibleSlides.length - 1));
        if (clampedIndex !== this.currentSlide) {
          this.currentSlide = clampedIndex;
          this.updateControls();
        }
      });

      // Arrow clicks
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => {
          const target = this.visibleSlides[this.currentSlide - 1];
          if (target) this.sliderTrack.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
        });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => {
          const target = this.visibleSlides[this.currentSlide + 1];
          if (target) this.sliderTrack.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
        });
      }

      // Navigate to active variant's image if set
      if (activeMediaId) {
        const index = this.visibleSlides.findIndex(
          (s) => s.dataset.mediaId === String(activeMediaId)
        );
        if (index > 0) {
          this.sliderTrack.scrollTo({ left: this.visibleSlides[index].offsetLeft });
          this.currentSlide = index;
        }
      }

      this.updateControls();
    }

    /**
     * Builds variant media grouping data from the product JSON,
     * independent of media-gallery. Auto-detects the color-type
     * option by matching common names (Color, Colour, Couleur, Farbe).
     * @param {object} product - Product data from variant-picker JSON
     * @returns {?object} - { groupOptionIndex, variantMedia: [...] } or null
     */
    buildVariantMediaData(product) {
      if (!product || !product.variants || !product.options) return null;

      // Auto-detect which option is the color/grouping option
      const colorNames = ['color', 'colour', 'couleur', 'farbe'];
      let groupOptionIndex = -1;

      for (let i = 0; i < product.options.length; i += 1) {
        if (colorNames.includes(product.options[i].toLowerCase())) {
          groupOptionIndex = i;
          break;
        }
      }

      // No color-type option found — no grouping needed
      if (groupOptionIndex === -1) return null;

      // Build variant → media mapping sorted by the group option
      const variantMedia = [];
      const seen = new Set();

      product.variants.forEach((variant) => {
        if (variant.featured_media && !seen.has(`${variant.featured_media.id}-${variant.options[groupOptionIndex]}`)) {
          seen.add(`${variant.featured_media.id}-${variant.options[groupOptionIndex]}`);
          variantMedia.push({
            variantId: variant.id,
            mediaId: variant.featured_media.id,
            option: variant.options[groupOptionIndex]
          });
        }
      });

      if (variantMedia.length === 0) return null;

      return { groupOptionIndex, variantMedia };
    }

    /**
     * Builds a map of option name → array of slide elements,
     * replicating media-gallery's grouping logic.
     */
    buildMediaGroupMap() {
      if (!this.variantMediaData || !this.variantMediaData.variantMedia) return;

      this.mediaGroupMap = {};
      let currentOption = null;

      // Walk through all slides in DOM order, assigning each to a group
      // based on the variantMedia data (same sequential logic as media-gallery.js)
      this.sliderSlides.forEach((slide) => {
        const mediaId = parseInt(slide.dataset.mediaId, 10);
        for (let i = 0; i < this.variantMediaData.variantMedia.length; i += 1) {
          if (mediaId === this.variantMediaData.variantMedia[i].mediaId) {
            if (currentOption !== this.variantMediaData.variantMedia[i].option) {
              currentOption = this.variantMediaData.variantMedia[i].option;
            }
          }
        }
        if (currentOption) {
          if (!this.mediaGroupMap[currentOption]) {
            this.mediaGroupMap[currentOption] = [];
          }
          this.mediaGroupMap[currentOption].push(slide);
        }
      });
    }

    /**
     * Gets the group name for a given media ID.
     */
    getGroupNameForMediaId(mediaId) {
      if (!this.variantMediaData || !this.variantMediaData.variantMedia) return null;
      const entry = this.variantMediaData.variantMedia.find((vm) => vm.mediaId === mediaId);
      return entry ? entry.option : null;
    }

    /**
     * Gets the group name for a given variant (from its featured_media).
     */
    getGroupNameForVariant(variant) {
      if (!variant.featured_media || !this.variantMediaData) return null;
      return this.getGroupNameForMediaId(variant.featured_media.id);
    }

    /**
     * Show only slides belonging to the given group, hide all others.
     * Replicates media-gallery's setActiveMediaGroup behavior.
     */
    setActiveMediaGroup(groupName) {
      if (!this.mediaGroupMap || !this.mediaGroupMap[groupName]) return;
      if (this.currentGroup === groupName) return;

      this.currentGroup = groupName;

      // Hide all slides
      this.sliderSlides.forEach((slide) => {
        slide.style.display = 'none';
      });

      // Show only slides in this group
      const groupSlides = this.mediaGroupMap[groupName];
      groupSlides.forEach((slide) => {
        slide.style.display = '';
      });

      this.visibleSlides = groupSlides;
      this.currentSlide = 0;

      // Reset scroll position
      if (this.sliderTrack) {
        this.sliderTrack.scrollTo({ left: 0 });
      }

      this.updateSliderVisibility();
      this.updateControls();
    }

    /**
     * Shows/hides arrows and counter based on visible slide count.
     */
    updateSliderVisibility() {
      const hasMultiple = this.visibleSlides.length > 1;
      if (this.prevBtn) this.prevBtn.style.display = hasMultiple ? '' : 'none';
      if (this.nextBtn) this.nextBtn.style.display = hasMultiple ? '' : 'none';
      const counter = this.querySelector('.quick-view-slider__counter');
      if (counter) counter.style.display = hasMultiple ? '' : 'none';
      if (this.sliderEl) {
        this.sliderEl.classList.toggle('quick-view-slider--single', !hasMultiple);
      }
    }

    updateControls() {
      const total = this.visibleSlides.length;
      if (this.prevBtn) this.prevBtn.disabled = this.currentSlide === 0;
      if (this.nextBtn) this.nextBtn.disabled = this.currentSlide === total - 1;
      if (this.counterCurrent) this.counterCurrent.textContent = this.currentSlide + 1;
      if (this.counterTotal) this.counterTotal.textContent = total;
    }

    updateImage(mediaId) {
      if (!this.visibleSlides || this.visibleSlides.length === 0) return;

      const index = this.visibleSlides.findIndex(
        (s) => s.dataset.mediaId === String(mediaId)
      );
      if (index >= 0) {
        this.currentSlide = index;
        this.sliderTrack.scrollTo({ left: this.visibleSlides[index].offsetLeft, behavior: 'smooth' });
        this.updateControls();
      }
    }

    getElementHtml(selector) {
      const el = this.productEl.querySelector(selector);
      return el ? el.innerHTML : '';
    }
  }

  customElements.define('quick-view-modal', QuickViewModal);
}
